import { IForm } from 'core-components/classes/models/IForm'
import { IColumn } from 'core-components/library/list/types'
import {
  BaseCrud,
  ICrudConstructor,
  IDataProviderFilter,
  IDataProviderSort,
  IDataSource,
  IPaging,
  IPagingFromTo,
  IQueryResult,
  Logic,
  TConnectorCreateFn,
  TConnectorDeleteBatchFn,
  TConnectorDeleteFn,
  TConnectorUpdateFn,
} from 'core-components/types'
import { IAddConnectors, IFetchArgs } from 'core-components/types/types'
import { getFormatDate } from 'src/core/classes/helpers/DateHelper'
import { proxy } from 'src/core/classes/helpers/UrlHelper'
import { Rest } from 'src/core/classes/http'
import { crudStoreEventBus, CrudStoreEvents, TCrudSyncCache } from 'src/core/types/crudStore'
import Logger from 'src/core/utils/Logger'

import Queue from './crud/Queue'

// T is used for flowed Data types, R is used for type of keyField
export class Crud<T = IForm, R = string | number> extends BaseCrud<T, R> {
  private connectorCreateFn?: TConnectorCreateFn<T>
  private connectorUpdateFn?: TConnectorUpdateFn<T>
  private connectorDeleteFn?: TConnectorDeleteFn<T>
  private connectorDeleteBatchFn?: TConnectorDeleteBatchFn<T>
  private calledQueryOperations: number = 0
  private isQueryCancelled: boolean = false

  constructor(props: ICrudConstructor<T, R>) {
    super(props)
    this.hierarchyField = props.hierarchyField || ''
    this.fetcher = props.fetcher
    this.keyField = props.keyField
    this.initialProps = { ...props }
    this.connectorUpdateFn = props.connectorUpdateFn
    this.connectorCreateFn = props.connectorCreateFn
    this.connectorDeleteFn = props.connectorDeleteFn
    this.connectorDeleteBatchFn = props.connectorDeleteBatchFn
    this.connectorFetchFn = props.connectorFetchFn
    this.skipPagingTransform = props.skipPagingTransform || false
    this.skipSubscribersCallOnFilterChange = props.skipSubscribersCallOnFilterChange || false
  }

  public clone = (): Crud<T, R> => {
    return new Crud(this.initialProps)
  }

  public async openNodes(values: (R | null)[]): Promise<IQueryResult<T, R>> {
    const data = await super.openNodes(values)
    this.updateItem({
      models: data.items,
      keyField: this.keyField,
      hierarchyField: this.hierarchyField,
      notifyToGlobalBus: true,
      notifyOtherInstances: false,
    })
    return data
  }

  public setConnectors = (connectors: IAddConnectors<T>) => {
    if (connectors.connectorCreateFn) {
      this.connectorCreateFn = connectors.connectorCreateFn
    }
    if (connectors.connectorUpdateFn) {
      this.connectorUpdateFn = connectors.connectorUpdateFn
    }
    if (connectors.connectorDeleteFn) {
      this.connectorDeleteFn = connectors.connectorDeleteFn
    }
    if (connectors.connectorDeleteBatchFn) {
      this.connectorDeleteBatchFn = connectors.connectorDeleteBatchFn
    }
    if (connectors.connectorFetchFn) {
      this.connectorFetchFn = connectors.connectorFetchFn
    }
  }

  public fetch = async (data: IFetchArgs = {}): Promise<IQueryResult<T, R>> => {
    const { sort, paging, pagingFromTo, query, filter } = this.getRequestParams(data)
    const { force, skipHierarchyFilter, skipTotal } = data

    const fetchedData = await this.query({
      query,
      filter,
      sort,
      paging: pagingFromTo,
      force: Boolean(force),
      skipHierarchyFilter: Boolean(skipHierarchyFilter),
      skipTotal: Boolean(skipTotal),
    })

    return this.cutQueryResult(fetchedData, paging)
  }

  public abort = () => {
    if (this.calledQueryOperations) {
      this.isQueryCancelled = true
      this.fetcher.abort && this.fetcher.abort()
    }
  }

  public setRoot(value: R | null): void {
    if (value !== this.hierarchyRoot) {
      this.hierarchyRoot = value
      this.callSubscribersWithInstance([])
    }
  }

  public export = async (fileName?: string, columns?: IColumn[]): Promise<void> => {
    if (this.fetcher.export) {
      const filter = this.combineFilter()
      const xlsxColums =
        columns &&
        columns.map((column) => {
          return {
            title: column.title,
            name: column.field,
          }
        })
      const result = await this.fetcher.export(filter, fileName, xlsxColums)
      const aLink = document.createElement('a')
      aLink.download = fileName
        ? `${fileName}.xlsx`
        : `${getFormatDate(new Date(), 'date4')}-export.xlsx`

      try {
        if (result.Blob) {
          aLink.href = window.URL.createObjectURL(new Blob([result.Blob]))
        } else if (result.Url) {
          const blob = await Rest.get({ url: proxy(result.Url) })
          aLink.href = window.URL.createObjectURL(blob)
        }
      } catch (err: unknown) {
        Logger.error('Error downloading file', (err as Error).message)
      }

      if (aLink.href) {
        document.body.appendChild(aLink)
        aLink.click()
        aLink.remove()
        window.URL.revokeObjectURL(aLink.href)
      }
    }
  }

  public saveToStore = async ({
    query,
    data,
    hierarchyField,
  }: {
    query: string
    data: {
      items: T[]
      total: number
    }
    hierarchyField?: string
  }): Promise<void> => {
    this.fetcher.saveToStore &&
      (await this.fetcher.saveToStore(query, data, hierarchyField, this.closeExpandedRoot))
  }

  public getDataFromStore = () => {
    return this.fetcher.getDataFromStore ? this.fetcher.getDataFromStore() : []
  }

  public async clear() {
    const dumpF = this.filters.current
    const dumpP = this.paging.current
    this.setFilter({ logic: Logic.AND, filters: [] })
    this.setPaging({
      page: 0,
      pageSize: 10000,
    })
    const list = await this.fetch()
    if (list.items) {
      list.items.forEach((item: any) => {
        this.delete(item[this.keyField], item, this.keyField)
      })
    }
    this.setFilter(dumpF || { logic: Logic.AND, filters: [] })
    this.setPaging(dumpP || this.paging.default)

    this.callSubscribersWithInstance({} as any)
  }

  /**
    Update item by keyField in loaded data. Use for replacing elements without load data from server.
    If item is new, then item will add to data.
   */
  public updateItem: TCrudSyncCache<T> = ({
    models,
    keyField = this.keyField,
    hierarchyField = this.hierarchyField,
    notifyToGlobalBus = true,
    notifyOtherInstances = true,
  }) => {
    if (this.fetcher.updateItem) {
      if (models instanceof Array) {
        models.forEach((model) => {
          this.fetcher.updateItem && this.fetcher.updateItem({ model, keyField, hierarchyField })
        })
      } else {
        this.fetcher.updateItem({ model: models, keyField, hierarchyField })
      }

      if (notifyToGlobalBus) {
        crudStoreEventBus.notify(
          CrudStoreEvents.onCrudChanged,
          'update',
          models,
          this,
          notifyOtherInstances
        )
      }

      if (notifyOtherInstances) {
        this.callSubscribersWithInstance([])
      }
    }
  }

  /**
    Remove item by keyField in loaded data. Use for remove elements without load data from server.
   */
  public deleteItem: TCrudSyncCache<T> = ({ models, keyField = this.keyField }): void => {
    if (this.fetcher.deleteItem) {
      if (models instanceof Array) {
        models.forEach((model) => {
          this.fetcher.deleteItem && this.fetcher.deleteItem({ model, keyField })
        })
      } else {
        this.fetcher.deleteItem({ model: models, keyField })
      }

      this.callSubscribersWithInstance([])
    }
  }

  public async clearData(callSubscribers?: boolean) {
    const list = await this.fetch()
    if (list.items) {
      list.items.forEach((item: any) => {
        this.delete(item[this.keyField], item, this.keyField)
      })
    }
    if (callSubscribers) {
      this.callSubscribersWithInstance({} as any)
    }
  }

  public create = async (formId: string | number | IDataSource, models: IForm[]): Promise<T[]> => {
    const isPlaneId = typeof formId === 'string' || typeof formId === 'number'
    const result = (await Queue.writeEnqueue(
      async () => await this.fetcher.create(isPlaneId ? parseInt(`${formId}`, 10) : formId, models)
    )) as T[]
    const mergedNewItems = this.connectorCreateFn
      ? await this.connectorCreateFn(isPlaneId ? `${formId}` : formId.formId, result)
      : result

    if (mergedNewItems.length && this.fetcher.saveToStore) {
      this.updateItem({
        models: mergedNewItems,
        keyField: this.keyField,
        hierarchyField: this.hierarchyField,
      })
      if (this.hierarchyField) {
        await this.fetchNodesWithChildren(mergedNewItems)
      }
    }

    if (this.hierarchyField && this.orderField) {
      this.fetcher.groupsSort && this.fetcher.groupsSort(this.orderField, this.keyField)
    }

    this.fetcher.subscribersCall && this.fetcher.subscribersCall(result)
    return result
  }

  public removeQueries = (callSubscribers: boolean = false) => {
    if (this.fetcher.removeQueries) {
      this.fetcher.removeQueries()
      if (callSubscribers && this.fetcher.subscribersCall) {
        this.fetcher.subscribersCall([])
      }
    }
  }

  public cleanCache = () => {
    if (this.fetcher.removeQueries) {
      this.fetcher.removeQueries()
      this.callSubscribersWithInstance([])
    }
  }

  public update = async (formId: string | number | IDataSource, models: IForm[]): Promise<T[]> => {
    const isPlaneId = typeof formId === 'string' || typeof formId === 'number'
    const result = (await Queue.writeEnqueue<T>(
      async () =>
        await this.fetcher.update(
          isPlaneId ? parseInt(`${formId}`, 10) : formId,
          models,
          this.keyField
        )
    )) as T[]
    const mergedFormsItems = this.connectorUpdateFn
      ? await this.connectorUpdateFn(isPlaneId ? `${formId}` : formId.formId, result)
      : result
    if (mergedFormsItems?.length && this.fetcher.saveToStore) {
      await this.fetcher.saveToStore(
        '',
        { items: mergedFormsItems, total: 0 },
        this.hierarchyField,
        this.closeExpandedRoot
      )
    }
    if (this.hierarchyField && this.orderField) {
      this.fetcher.groupsSort && this.fetcher.groupsSort(this.orderField, this.keyField)
    }
    if (mergedFormsItems?.length) {
      crudStoreEventBus.notify(CrudStoreEvents.onCrudChanged, 'update', mergedFormsItems, this)
    }
    this.fetcher.subscribersCall && this.fetcher.subscribersCall(result)
    return result
  }

  public delete = async (
    formId: string | number | IDataSource,
    model: IForm,
    keyField?: string
  ): Promise<boolean> => {
    const isPlaneId = typeof formId === 'string' || typeof formId === 'number'
    if (this.fetcher.delete) {
      const result = (await Queue.writeEnqueue(async () => {
        if (!this.fetcher.delete) {
          return false
        }

        const result = await this.fetcher.delete(
          isPlaneId ? parseInt(`${formId}`, 10) : formId,
          model[keyField || this.keyField],
          keyField || this.keyField
        )

        if (result) {
          crudStoreEventBus.notify(CrudStoreEvents.onCrudChanged, 'delete', model, this)
        }

        return result
      })) as boolean

      if (this.connectorDeleteFn) {
        const mergedItems = await this.connectorDeleteFn(
          isPlaneId ? `${formId}` : formId.formId,
          model as T
        )
        if (mergedItems && this.fetcher.saveToStore) {
          await this.fetcher.saveToStore(
            '',
            { items: mergedItems, total: 0 },
            this.hierarchyField,
            this.closeExpandedRoot
          )
        }
      }
      this.fetcher.subscribersCall && this.fetcher.subscribersCall(result)
      return result
    }
    return false
  }

  public deleteBatch = async (
    formId: string | IDataSource,
    model: IForm[],
    keyField?: string
  ): Promise<boolean> => {
    const deleteBatch = this.fetcher.deleteBatch

    if (deleteBatch) {
      const batchWrapper = async () => {
        return deleteBatch(
          typeof formId === 'string' ? parseInt(formId, 10) : formId,
          model.map((item) => item[keyField || this.keyField]),
          keyField || this.keyField
        )
      }

      const result = (await Queue.writeEnqueue(batchWrapper)) as boolean

      if (this.connectorDeleteBatchFn) {
        const mergedItems = await this.connectorDeleteBatchFn(
          typeof formId === 'string' ? formId : formId.formId,
          model as T[]
        )
        if (mergedItems && this.fetcher.saveToStore) {
          await this.fetcher.saveToStore(
            '',
            { items: mergedItems, total: 0 },
            this.hierarchyField,
            this.closeExpandedRoot
          )
          this.removeQueries()
        }
      }
      this.fetcher.subscribersCall && this.fetcher.subscribersCall(result)
      return result
    }
    return false
  }

  protected async query({
    query,
    filter,
    sort,
    paging,
    force = false,
    skipHierarchyFilter = false,
    skipTotal = false,
  }: {
    query: string
    filter: IDataProviderFilter
    sort: IDataProviderSort | IDataProviderSort[] | undefined
    paging: IPaging | IPagingFromTo
    force?: boolean
    skipHierarchyFilter?: boolean
    skipTotal?: boolean
  }): Promise<IQueryResult<T, R>> {
    this.calledQueryOperations++

    const res = await Queue.read<T, R>(query, this, async () => {
      if (this.isQueryCancelled) {
        return {
          items: [],
          total: 0,
        }
      }
      const expanded = this.getExpandedWithoutIgnored()
      return await this.fetcher.read(
        query,
        filter,
        sort,
        paging,
        this.keyField,
        this.connectorFetchFn,
        this.hierarchyField,
        expanded,
        this.hierarchyRoot,
        force,
        skipHierarchyFilter,
        this.orderField,
        this.closeExpandedRoot,
        this.audience,
        skipTotal
      )
    })
    res.total = res.total || res.items?.length || 0
    res.groups = res.groups || []

    this.calledQueryOperations--

    if (this.isQueryCancelled) {
      if (!this.calledQueryOperations) {
        this.isQueryCancelled = false
      }
      throw new Error('Operation canceled by the user')
    }
    return res as IQueryResult<T, R>
  }

  private cutQueryResult = (fetchedData: IQueryResult<T, R>, paging: IPaging) => {
    const copy = { ...fetchedData }
    if (fetchedData.total > paging.pageSize) {
      copy.items = fetchedData.items.slice(0, fetchedData.total - 1)
    } else {
      copy.items = fetchedData.items ? fetchedData.items.slice() : []
    }
    return copy
  }

  private fetchNodesWithChildren = async (mergedNewItems: T[]) => {
    const withChildrenIds = mergedNewItems
      .filter((el) => {
        return (el as IForm).hasChildren
      })
      .map((el) => el[this.keyField])
    withChildrenIds.forEach((id) => this.ignoreKeys.delete(id))
    await this.openNodes(withChildrenIds)
  }
}
